/**
 * Called from the default branch when an unexpected value reaches the switch at
 * runtime (i.e. untyped/wire/`as`-cast data violated the static type). Optional:
 * static callsites that trust the compiler's exhaustiveness check omit it and
 * get no logging.
 */
type OnUnexpected = (value: unknown, defaultValue: unknown) => void;

interface Options<DefaultValue> {
  /** Value returned from the default branch. Defaults to `undefined`. */
  defaultValue?: DefaultValue;
  /** Runtime safety-net; called with `(value, defaultValue)` before returning. */
  onUnexpected?: OnUnexpected;
}

/**
 * Exhaustiveness helper for `switch`/branching over a union. Statically enforces
 * that every case is handled (the `value: never` overload); at runtime returns
 * `options.defaultValue` and optionally invokes `options.onUnexpected`.
 *
 * @example
 *  switch (foo) {
 *    // ...handle every case
 *    default:
 *      return exhaustiveSwitch(foo);
 *  }
 *
 * @example
 *  // with a default and a runtime safety-net log
 *  default:
 *    return exhaustiveSwitch(foo, {
 *      defaultValue: null,
 *      onUnexpected: (value, defaultValue) =>
 *        logger.logError(new Error("unexpected value"), {
 *          data: { defaultValue, unexpectedValue: value },
 *        }),
 *    });
 *
 * @deprecated
 * This function (created Jan 2019) mixes static exhaustiveness checking with runtime default return values and rare policy-based error handling/extras.
 * Callers should switch to using a `satisfies never` check for exhaustiveness and loggign/returning the default value themselves:
 *
 * ```ts
 *  switch (foo) {
 *    // ...handle every case
 *    default:
 *      foo satisfies never;
 *      return undefined; // or null, or ""
 *  }
 * ```
 */
export function exhaustiveSwitch<DefaultValue = undefined>(
  value: never,
  options?: Options<DefaultValue>
): DefaultValue;

export function exhaustiveSwitch<
  Unhandled extends string = never,
  DefaultValue = undefined,
>(value: Unhandled | never, options?: Options<DefaultValue>): DefaultValue {
  const defaultValue = options?.defaultValue;
  options?.onUnexpected?.(value, defaultValue);
  return defaultValue as DefaultValue;
}
